const BACKEND_URI = ((import.meta as any)?.env?.VITE_BACKEND_URI ?? "").replace(/\/$/, "");

import { Location, LocationMember, SetLocationMembersResponse } from "./models";
import { getHeaders } from "./api";

export interface LocationsResponse {
    locations: Location[];
    assigned_location_ids: string[];
}

export async function getLocationsApi(idToken: string | undefined): Promise<LocationsResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/locations`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
        const text = (await response.text()).trim().toLowerCase();
        if (text.startsWith("<!doctype") || text.startsWith("<html")) {
            throw Error("Locations API returned HTML instead of JSON");
        }
        throw Error("Locations API returned a non-JSON response");
    }

    return (await response.json()) as LocationsResponse;
}

export async function createLocationApi(
    payload: { name: string; address?: string },
    idToken: string | undefined
): Promise<Location> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/locations`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as Location;
}

export async function updateLocationApi(
    locationId: string,
    payload: { name?: string; address?: string | null },
    idToken: string | undefined
): Promise<Location> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/locations/${locationId}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as Location;
}

export async function deleteLocationApi(locationId: string, idToken: string | undefined): Promise<void> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/locations/${locationId}`, {
        method: "DELETE",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
}

export async function getLocationMembersApi(locationId: string, idToken: string | undefined): Promise<LocationMember[]> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/locations/${locationId}/members`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    const data = (await response.json()) as { members: LocationMember[] };
    return data.members || [];
}

export async function assignUserToLocationApi(
    locationId: string,
    userId: string,
    idToken: string | undefined
): Promise<void> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/locations/${locationId}/members`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
}

export async function removeUserFromLocationApi(
    locationId: string,
    userId: string,
    idToken: string | undefined
): Promise<void> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/locations/${locationId}/members/${userId}`, {
        method: "DELETE",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
}

export async function setLocationMembersApi(
    locationId: string,
    userIds: string[],
    idToken: string | undefined
): Promise<SetLocationMembersResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/locations/${locationId}/members`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ user_ids: userIds }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        let message = `Request failed with status ${response.status}: ${response.statusText}`;
        try {
            const errorBody = await response.json();
            if (errorBody?.error && typeof errorBody.error === "string") {
                message = errorBody.error;
            }
        } catch {
            // Keep status-based error message on parse failures
        }
        throw Error(message);
    }

    return (await response.json()) as SetLocationMembersResponse;
}
